import { Section } from "../components/Bits";
import { useRankedMarkets } from "../lib/data";
import { centsDelta, usd } from "../lib/format";
import { multiple } from "../lib/unusual";

const pts = (v: number) => `${v > 0 ? "+" : v < 0 ? "−" : ""}${Math.abs(v).toFixed(0)}`;

function Line({ label, detail, value }: { label: string; detail: string; value: string }) {
	return (
		<div className="grid grid-cols-[4rem_minmax(0,1fr)_auto] items-baseline gap-2 border-b border-line/50 px-2 py-1 text-[11px] last:border-b-0">
			<span className="text-dim">{label}</span>
			<span className="truncate text-fg" title={detail}>{detail}</span>
			<span className="num font-bold text-bright">{value}</span>
		</div>
	);
}

/** Why a market sits where it does on Unusual Markets. Quiet when it is not in the loaded set. */
export function ScoreWhy({ conditionId }: { conditionId: string }) {
	const { ranked } = useRankedMarkets();
	const row = ranked.find((r) => r.market.conditionId === conditionId);
	if (!row) return null;
	const { market: m, parts: p } = row;
	return (
		<Section title="unusual score" note={p ? `peers: ${p.peerLabel}, ${p.peerCount} markets` : "not scored"}>
			{p ? (
				<>
					<Line
						label="volume"
						detail={`${multiple(m.volX, p.thinBaseline)} its usual ${usd(m.baselineDaily)} a day${p.thinBaseline ? " (under $1K, counted as $1K)" : ""}`}
						value={pts(p.volumePts)}
					/>
					<Line label="peers" detail={`${p.peerLabel} median is ${multiple(p.peerVolX, false)} today`} value={pts(p.peerPts)} />
					<Line label="price" detail={`${centsDelta(m.change24)} in 24h, against ${p.peerLabel}'s typical move`} value={pts(p.pricePts)} />
					{p.discountWhy ? <Line label="discount" detail={p.discountWhy} value={`×${p.discount.toFixed(2)}`} /> : null}
					<Line label="score" detail={row.folded ? "ranked under a higher scorer from the same event" : "out of 100"} value={p.score.toFixed(0)} />
				</>
			) : (
				<div className="px-2 py-1 text-[11px] leading-5 text-dim">{row.note}: volume here follows the calendar or has no history, so it is listed separately.</div>
			)}
		</Section>
	);
}
